
// GSTR1 Helper Functions and Constants

export const invoiceTypeOptions = [
    { value: 'Regular (B2B/B2C)', label: 'Regular (B2B/B2C)', code: 'R' },
    { value: 'Export Without payment of tax', label: 'Export Without payment of tax', code: 'WOPAY', isExport: true },
    { value: 'Export with payment of tax', label: 'Export with payment of tax', code: 'WPAY', isExport: true },
    { value: 'Deemed Export', label: 'Deemed Export', code: 'DE' },
    { value: 'SEZ supply with payment of Tax', label: 'SEZ supply with payment of Tax', code: 'SEWP' },
    { value: 'SEZ supply without payment of Tax', label: 'SEZ supply without payment of Tax', code: 'SEWOP', taxExempt: true },
    { value: 'Exempt', label: 'Exempt', code: 'EXP', taxExempt: true },
    { value: 'Non-GST Supply', label: 'Non-GST Supply', code: 'NGSUP', taxExempt: true }
]

export const invoiceTypeMigration = {
    'Regular B2B': 'Regular (B2B/B2C)',
    'SEZ supplies with payment': 'SEZ supply with payment of Tax',
    'SEZ supplies without payment': 'SEZ supply without payment of Tax',
    'Deemed Exp': 'Deemed Export',
    'Intra-State supplies attracting IGST': 'Regular (B2B/B2C)'
}

export const migrateInvoiceType = (oldInvoiceType) => {
    return invoiceTypeMigration[oldInvoiceType] || oldInvoiceType
}

export const isTaxExemptInvoiceType = (invoiceType) => {
    if (!invoiceType) return false
    const typeStr = invoiceType.toString().toUpperCase().trim()
    const option = invoiceTypeOptions.find(opt => 
        opt.value.toUpperCase() === typeStr || 
        opt.code.toUpperCase() === typeStr
    )
    return option ? option.taxExempt : (typeStr === 'EXEMPT' || typeStr === 'NIL' || typeStr === 'NGSUP')
}

export const isExportInvoiceType = (invoiceType) => {
    if (!invoiceType) return false
    
    let typeStr = ''
    if (typeof invoiceType === 'object') {
        typeStr = (invoiceType.value || invoiceType.label || '').toString().toUpperCase().trim()
    } else {
        typeStr = invoiceType.toString().toUpperCase().trim()
    }
    
    if (!typeStr) return false
    
    // NUCLEAR OPTION: If it contains EXPORT, WOPAY, or WPAY, it's an export. PERIOD.
    if (typeStr.includes('EXPORT') || typeStr.includes('WOPAY') || typeStr.includes('WPAY') || typeStr.includes('EXP')) {
        return true
    }
    
    // Fallback to options
    const option = invoiceTypeOptions.find(opt => 
        opt.value.toUpperCase() === typeStr || 
        opt.code.toUpperCase() === typeStr
    )
    return option ? option.isExport : false
}

export const getInvoiceTypeCode = (invoiceType) => {
    if (!invoiceType) return 'R'
    
    let typeStr = ''
    if (typeof invoiceType === 'object') {
        typeStr = (invoiceType.value || invoiceType.label || '').toString().toUpperCase().trim()
    } else {
        typeStr = invoiceType.toString().toUpperCase().trim()
    }
    
    if (!typeStr) return 'R'

    // Robust matching for Exports
    if (typeStr.includes('EXPORT') || typeStr.includes('WOPAY') || typeStr.includes('WPAY') || typeStr.includes('EXP')) {
        if (typeStr.includes('WITHOUT') || typeStr.includes('WOPAY')) return 'WOPAY'
        if (typeStr.includes('WITH') || typeStr.includes('WPAY')) return 'WPAY'
        // Fallback for export if neither is clear
        return 'WOPAY'
    }

    const option = invoiceTypeOptions.find(opt => 
        opt.value.toUpperCase() === typeStr || 
        opt.code.toUpperCase() === typeStr
    )
    if (option) return option.code
    
    return 'R'
}

export const isHsnService = (hsnCode) => {
    return hsnCode && hsnCode.toString().trim().startsWith('99')
}

export const formatDateForJson = (dateStr) => {
    if (!dateStr) return ''
    // If already in DD-MM-YYYY, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr
    // If in YYYY-MM-DD, convert to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-')
        return `${day}-${month}-${year}`
    }
    return dateStr
}

export const groupInvoicesByPrefix = (invoices) => {
    const groups = {}

    invoices.forEach(invoice => {
        // Extract prefix (everything before the last numeric part)
        const match = invoice.toString().match(/^(.+?)(\d+)$/)
        if (match) {
            const prefix = match[1] || '' // Everything before the number
            if (!groups[prefix]) {
                groups[prefix] = []
            }
            groups[prefix].push(invoice)
        } else {
            // No clear prefix, treat as one group
            if (!groups['']) {
                groups[''] = []
            }
            groups[''].push(invoice)
        }
    })

    return groups
}

export const extractNumericPart = (invoice) => {
    const match = invoice.toString().match(/(\d+)$/)
    return match ? parseInt(match[1]) : 0
}

export const getPosEnum = (posCode) => {
    if (!posCode) return ''
    // Simple pass-through or normalization if needed
    // Based on Gstr1Form logic, it simply returns the code if valid
    // Ensure we strip any labels if passed
    if (posCode.includes('-')) return posCode.split('-')[0]
    return posCode
}

export const getPosDisplayName = (posCode) => {
    const posCodeToName = {
        '01': '01-Jammu & Kashmir', '02': '02-Himachal Pradesh', '03': '03-Punjab', '04': '04-Chandigarh', '05': '05-Uttarakhand',
        '06': '06-Haryana', '07': '07-Delhi', '08': '08-Rajasthan', '09': '09-Uttar Pradesh', '10': '10-Bihar', '11': '11-Sikkim',
        '12': '12-Arunachal Pradesh', '13': '13-Nagaland', '14': '14-Manipur', '15': '15-Mizoram', '16': '16-Tripura',
        '17': '17-Meghalaya', '18': '18-Assam', '19': '19-West Bengal', '20': '20-Jharkhand', '21': '21-Odisha', '22': '22-Chhattisgarh',
        '23': '23-Madhya Pradesh', '24': '24-Gujarat', '25': '25-Daman & Diu', '26': '26-Dadra & Nagar Haveli', '27': '27-Maharashtra',
        '29': '29-Karnataka', '30': '30-Goa', '31': '31-Lakshdweep', '32': '32-Kerala', '33': '33-Tamil Nadu', '34': '34-Puducherry',
        '35': '35-Andaman & Nicobar Islands', '36': '36-Telangana', '37': '37-Andhra Pradesh', '38': '38-Ladakh', '97': '97-Other Territory'
    }
    return posCodeToName[posCode] || posCode
}

export const isInterStateTransaction = (gstinMaster, placeOfSupply) => {
    if (!gstinMaster || !placeOfSupply) return false
    const gstinMasterFirstTwo = gstinMaster.substring(0, 2)
    const posEnum = getPosEnum(placeOfSupply)
    return gstinMasterFirstTwo !== posEnum
}

export const getUqcEnum = (uqcValue) => {
    // If it's already an enum (3 characters), return as is
    if (uqcValue && uqcValue.length === 3) {
        return uqcValue.toUpperCase()
    }

    // If it's a full name, find the corresponding enum
    const uqcOption = uqcOptions.find(option =>
        option.label.toUpperCase() === uqcValue.toUpperCase() ||
        option.label.toUpperCase().includes(uqcValue.toUpperCase())
    )

    return uqcOption ? uqcOption.value : uqcValue
}

export const uqcOptions = [
    { value: 'BAG', label: 'BAG-BAGS' },
    { value: 'BAL', label: 'BAL-BALE' },
    { value: 'BDL', label: 'BDL-BUNDLES' },
    { value: 'BKL', label: 'BKL-BUCKLES' },
    { value: 'BOU', label: 'BOU-BILLION OF UNITS' },
    { value: 'BOX', label: 'BOX-BOX' },
    { value: 'BTL', label: 'BTL-BOTTLES' },
    { value: 'BUN', label: 'BUN-BUNCHES' },
    { value: 'CAN', label: 'CAN-CANS' },
    { value: 'CBM', label: 'CBM-CUBIC METERS' },
    { value: 'CCM', label: 'CCM-CUBIC CENTIMETERS' },
    { value: 'CMS', label: 'CMS-CENTIMETERS' },
    { value: 'CTN', label: 'CTN-CARTONS' },
    { value: 'DOZ', label: 'DOZ-DOZENS' },
    { value: 'DRM', label: 'DRM-DRUMS' },
    { value: 'GGK', label: 'GGK-GREAT GROSS' },
    { value: 'GMS', label: 'GMS-GRAMMES' },
    { value: 'GRS', label: 'GRS-GROSS' },
    { value: 'GYD', label: 'GYD-GROSS YARDS' },
    { value: 'KGS', label: 'KGS-KILOGRAMS' },
    { value: 'KLR', label: 'KLR-KILOLITRE' },
    { value: 'KME', label: 'KME-KILOMETRE' },
    { value: 'LTR', label: 'LTR-LITRES' },
    { value: 'MLT', label: 'MLT-MILILITRE' },
    { value: 'MTR', label: 'MTR-METERS' },
    { value: 'MTS', label: 'MTS-METRIC TON' },
    { value: 'NOS', label: 'NOS-NUMBERS' },
    { value: 'PAC', label: 'PAC-PACKS' },
    { value: 'PCS', label: 'PCS-PIECES' },
    { value: 'PRS', label: 'PRS-PAIRS' },
    { value: 'QTL', label: 'QTL-QUINTAL' },
    { value: 'ROL', label: 'ROL-ROLLS' },
    { value: 'SET', label: 'SET-SETS' },
    { value: 'SQF', label: 'SQF-SQUARE FEET' },
    { value: 'SQM', label: 'SQM-SQUARE METERS' },
    { value: 'SQY', label: 'SQY-SQUARE YARDS' },
    { value: 'TBS', label: 'TBS-TABLETS' },
    { value: 'TGM', label: 'TGM-TEN GROSS' },
    { value: 'THD', label: 'THD-THOUSANDS' },
    { value: 'TON', label: 'TON-TONNES' },
    { value: 'TUB', label: 'TUB-TUBES' },
    { value: 'UGS', label: 'UGS-US GALLONS' },
    { value: 'UNT', label: 'UNT-UNITS' },
    { value: 'YDS', label: 'YDS-YARDS' },
    { value: 'OTH', label: 'OTH-OTHERS' },
    { value: 'NA', label: 'NA-NOT APPLICABLE' }
]

// Unified JSON generation function
export const generateUnifiedJson = (gstinMaster, gstinData, sections = null, month, year) => {
    // Input validation
    if (!gstinMaster || !gstinData || !Array.isArray(gstinData)) {
        console.error('Invalid input parameters for JSON generation')
        return null
    }

    if (gstinData.length === 0) {
        console.warn(`No data available for GSTIN: ${gstinMaster}`)
        return null
    }

    // Use all sections if no sections specified
    const selectedSections = sections || {
        b2b: true,
        b2cl: true,
        b2cs: true,
        cdnr: true,
        cdnur: true,
        hsn: true,
        doc_issue: true,
        exp: true,
        sezwp: true,
        sezwop: true,
        nil: true,
        all: true
    }

    try {
        // Get current financial year and period
        // Ensure month/year are passed
        if (!month || !year) {
            console.error('Month and Year are required for GSTR1 JSON generation');
            return null;
        }
        const fp = `${month.toString().padStart(2, '0')}${year}`

        const jsonData = {
            gstin: gstinMaster,
            fp: fp,
            version: "GST3.2.4",
            hash: "hash"
        }

        // Separate regular invoices and credit notes
        const regularInvoices = gstinData.filter(row => row.invoiceValue >= 0)
        const creditNotes = gstinData.filter(row => row.invoiceValue < 0)

        // Separate B2B and B2C transactions (regular invoices only, excluding tax-exempt, export, and nil-rated)
        const b2bTransactions = regularInvoices.filter(row =>
            row.gstinCustomer && row.gstinCustomer.trim() !== '' &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType) && parseFloat(row.rate) !== 0
        )
        const b2cTransactions = regularInvoices.filter(row =>
            (!row.gstinCustomer || row.gstinCustomer.trim() === '') &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType) && parseFloat(row.rate) !== 0
        )

        // Helper to calculate total value per invoice to properly check B2CL limit
        const getInvoiceTotals = (transactions) => {
            const totals = {};
            transactions.forEach(row => {
                if (!totals[row.invoiceNo]) {
                    totals[row.invoiceNo] = 0;
                }
                const rowTotal = (parseFloat(row.taxableValue) || 0) + 
                                 (parseFloat(row.igst) || 0) + 
                                 (parseFloat(row.cgst) || 0) + 
                                 (parseFloat(row.sgst) || 0) + 
                                 (parseFloat(row.cess) || 0);
                totals[row.invoiceNo] += Math.abs(rowTotal);
            });
            return totals;
        };

        const b2cTotals = getInvoiceTotals(b2cTransactions);
        
        // Separate B2CL and B2CS (regular invoices only)
        const b2clTransactions = b2cTransactions.filter(row =>
            b2cTotals[row.invoiceNo] > 100000 && isInterStateTransaction(row.gstinMaster, row.placeOfSupply)
        )
        const b2csTransactions = b2cTransactions.filter(row =>
            b2cTotals[row.invoiceNo] <= 100000 || !isInterStateTransaction(row.gstinMaster, row.placeOfSupply)
        )

        // Separate export credit notes
        const exportCreditNotes = creditNotes.filter(row => isExportInvoiceType(row.invoiceType))

        // Separate credit notes by B2B and B2C (excluding tax-exempt, export, and nil-rated)
        const b2bCreditNotes = creditNotes.filter(row =>
            row.gstinCustomer && row.gstinCustomer.trim() !== '' &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType) && parseFloat(row.rate) !== 0
        )
        const b2cCreditNotes = creditNotes.filter(row =>
            (!row.gstinCustomer || row.gstinCustomer.trim() === '') &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType) && parseFloat(row.rate) !== 0
        )

        const b2cCreditTotals = getInvoiceTotals(b2cCreditNotes);

        // Separate B2CL and B2CS credit notes
        const b2clCreditNotes = b2cCreditNotes.filter(row =>
            b2cCreditTotals[row.invoiceNo] > 100000 && isInterStateTransaction(row.gstinMaster, row.placeOfSupply)
        )
        const b2csCreditNotes = b2cCreditNotes.filter(row =>
            b2cCreditTotals[row.invoiceNo] <= 100000 || !isInterStateTransaction(row.gstinMaster, row.placeOfSupply)
        )

        // Process B2B transactions
        if (b2bTransactions.length > 0 && selectedSections.b2b) {
            const groupedB2B = {}
            b2bTransactions.forEach(row => {
                const key = `${row.gstinCustomer}_${row.invoiceNo}`
                if (!groupedB2B[key]) {
                    groupedB2B[key] = {
                        ctin: row.gstinCustomer,
                        inv: [
                            {
                                inum: row.invoiceNo,
                                idt: formatDateForJson(row.invoiceDate),
                                val: 0,
                                pos: getPosEnum(row.placeOfSupply),
                                rchrg: row.reverseCharge || 'N',
                                inv_typ: getInvoiceTypeCode(row.invoiceType),
                                itms: [],
                                itmsGroupedByRate: {}
                            }
                        ]
                    }
                }

                // Add to total invoice value
                groupedB2B[key].inv[0].val = Math.round((groupedB2B[key].inv[0].val + (parseFloat(row.invoiceValue) || 0)) * 100) / 100

                // Club items by rate within the invoice
                const invRef = groupedB2B[key].inv[0]
                const rateKey = String(row.rate)
                if (!invRef.itmsGroupedByRate[rateKey]) {
                    let rt = parseFloat(row.rate);
                    if (isNaN(rt)) rt = 0;
                    invRef.itmsGroupedByRate[rateKey] = {
                        rt: rt,
                        txval: 0,
                        iamt: 0,
                        camt: 0,
                        samt: 0,
                        csamt: 0
                    }
                }
                const agg = invRef.itmsGroupedByRate[rateKey]
                agg.txval += Math.round(row.taxableValue * 100) / 100
                agg.iamt += Math.round(row.igst * 100) / 100
                agg.camt += Math.round(row.cgst * 100) / 100
                agg.samt += Math.round(row.sgst * 100) / 100
                agg.csamt += Math.round(row.cess * 100) / 100
            })

            jsonData.b2b = Object.values(groupedB2B).map(group => ({
                ctin: group.ctin,
                inv: group.inv.map(inv => ({
                    inum: inv.inum,
                    idt: inv.idt,
                    val: inv.val,
                    pos: inv.pos,
                    rchrg: inv.rchrg,
                    inv_typ: inv.inv_typ,
                    itms: Object.values(inv.itmsGroupedByRate).map((g, index) => ({
                        num: index + 1,
                        itm_det: {
                            rt: g.rt,
                            txval: Math.round(g.txval * 100) / 100,
                            iamt: Math.round(g.iamt * 100) / 100,
                            camt: Math.round(g.camt * 100) / 100,
                            samt: Math.round(g.samt * 100) / 100,
                            csamt: Math.round(g.csamt * 100) / 100
                        }
                    }))
                }))
            }))
        }

        // Process B2CL transactions
        if (b2clTransactions.length > 0 && selectedSections.b2cl) {
            const groupedByPos = {}
            b2clTransactions.forEach(row => {
                const pos = getPosEnum(row.placeOfSupply)
                if (!groupedByPos[pos]) {
                    groupedByPos[pos] = {}
                }

                const invoiceKey = row.invoiceNo
                if (!groupedByPos[pos][invoiceKey]) {
                    groupedByPos[pos][invoiceKey] = {
                        inum: row.invoiceNo,
                        idt: formatDateForJson(row.invoiceDate),
                        val: 0,
                        itms: [],
                        itmsGroupedByRate: {}
                    }
                }

                // Add to total invoice value
                groupedByPos[pos][invoiceKey].val = Math.round((groupedByPos[pos][invoiceKey].val + (parseFloat(row.invoiceValue) || 0)) * 100) / 100

                const invAgg = groupedByPos[pos][invoiceKey]
                const rateKey = String(row.rate)
                if (!invAgg.itmsGroupedByRate[rateKey]) {
                    let rt = parseFloat(row.rate);
                    if (isNaN(rt)) rt = 0;
                    invAgg.itmsGroupedByRate[rateKey] = {
                        rt: rt,
                        txval: 0,
                        iamt: 0,
                        csamt: 0
                    }
                }
                const agg = invAgg.itmsGroupedByRate[rateKey]
                agg.txval += Math.round(row.taxableValue * 100) / 100
                agg.iamt += Math.round(row.igst * 100) / 100
                agg.csamt += Math.round(row.cess * 100) / 100
            })

            jsonData.b2cl = Object.entries(groupedByPos).map(([pos, invoices]) => ({
                pos: pos,
                inv: Object.values(invoices).map(inv => ({
                    idt: inv.idt,
                    inum: inv.inum,
                    val: inv.val,
                    itms: Object.values(inv.itmsGroupedByRate).map((g, index) => ({
                        num: index + 1,
                        itm_det: {
                            txval: Math.round(g.txval * 100) / 100,
                            rt: g.rt,
                            csamt: Math.round(g.csamt * 100) / 100,
                            iamt: Math.round(g.iamt * 100) / 100
                        }
                    }))
                }))
            }))
        }

        // Group B2CS transactions by rate and place of supply (including credit notes)
        const b2csGrouped = {}
        const allB2csTransactions = [...b2csTransactions, ...b2csCreditNotes]
        allB2csTransactions.forEach(row => {
            const key = `${row.rate}_${row.placeOfSupply}`
            if (!b2csGrouped[key]) {
                let rt = parseFloat(row.rate);
                if (isNaN(rt)) rt = 0;
                b2csGrouped[key] = {
                    rt: rt,
                    pos: row.placeOfSupply,
                    txval: 0,
                    iamt: 0,
                    camt: 0,
                    samt: 0,
                    csamt: 0
                }
            }
            // For B2CS, net the values (regular invoices + credit notes)
            b2csGrouped[key].txval += row.taxableValue
            b2csGrouped[key].iamt += row.igst
            b2csGrouped[key].camt += row.cgst
            b2csGrouped[key].samt += row.sgst
            b2csGrouped[key].csamt += row.cess
        })

        if (Object.keys(b2csGrouped).length > 0 && selectedSections.b2cs) {
            jsonData.b2cs = Object.values(b2csGrouped).map(group => {
                const gstinMasterFirstTwo = gstinMaster.substring(0, 2)
                const posEnum = getPosEnum(group.pos)
                const sply_ty = gstinMasterFirstTwo === posEnum ? "INTRA" : "INTER"

                return {
                    sply_ty: sply_ty,
                    rt: group.rt,
                    typ: "OE",
                    pos: posEnum,
                    txval: Math.round(group.txval * 100) / 100,
                    iamt: Math.round(group.iamt * 100) / 100,
                    camt: Math.round(group.camt * 100) / 100,
                    samt: Math.round(group.samt * 100) / 100,
                    csamt: Math.round(group.csamt * 100) / 100
                }
            })
        }

        // Process HSN
        const hsnB2BData = {}
        const allB2BTransactions = [...b2bTransactions, ...b2bCreditNotes]
        const taxExemptB2B = gstinData.filter(row =>
            isTaxExemptInvoiceType(row.invoiceType) && row.gstinCustomer && row.gstinCustomer.trim() !== ''
        )
        const exportB2B = gstinData.filter(row =>
            isExportInvoiceType(row.invoiceType) && row.gstinCustomer && row.gstinCustomer.trim() !== ''
        )
        const nilRatedB2B = gstinData.filter(row =>
            parseFloat(row.rate) === 0 && row.gstinCustomer && row.gstinCustomer.trim() !== '' &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType)
        )
        const allB2BForHSN = [...allB2BTransactions, ...taxExemptB2B, ...exportB2B, ...nilRatedB2B]
        allB2BForHSN.forEach(row => {
            const key = `${row.hsn}_${row.rate}`
            if (!hsnB2BData[key]) {
                let rt = parseFloat(row.rate);
                if (isNaN(rt)) rt = 0;
                hsnB2BData[key] = {
                    hsn_sc: row.hsn,
                    desc: '',
                    uqc: row.uqc,
                    qty: 0,
                    rt: rt,
                    txval: 0,
                    iamt: 0,
                    samt: 0,
                    camt: 0,
                    csamt: 0
                }
            }
            hsnB2BData[key].qty += parseFloat(row.quantity) || 0
            hsnB2BData[key].txval += Math.round(row.taxableValue * 100) / 100
            hsnB2BData[key].iamt += Math.round(row.igst * 100) / 100
            hsnB2BData[key].samt += Math.round(row.sgst * 100) / 100
            hsnB2BData[key].camt += Math.round(row.cgst * 100) / 100
            hsnB2BData[key].csamt += Math.round(row.cess * 100) / 100
        })

        const hsnB2CData = {}
        const allB2CTransactions = [...b2clTransactions, ...b2clCreditNotes, ...b2csTransactions, ...b2csCreditNotes]
        const taxExemptB2C = gstinData.filter(row =>
            isTaxExemptInvoiceType(row.invoiceType) && (!row.gstinCustomer || row.gstinCustomer.trim() === '')
        )
        const exportB2C = gstinData.filter(row =>
            isExportInvoiceType(row.invoiceType) && (!row.gstinCustomer || row.gstinCustomer.trim() === '')
        )
        const nilRatedB2C = gstinData.filter(row =>
            parseFloat(row.rate) === 0 && (!row.gstinCustomer || row.gstinCustomer.trim() === '') &&
            !isTaxExemptInvoiceType(row.invoiceType) && !isExportInvoiceType(row.invoiceType)
        )
        const allB2CForHSN = [...allB2CTransactions, ...taxExemptB2C, ...exportB2C, ...nilRatedB2C]
        allB2CForHSN.forEach(row => {
            const key = `${row.hsn}_${row.rate}`
            if (!hsnB2CData[key]) {
                let rt = parseFloat(row.rate);
                if (isNaN(rt)) rt = 0;
                hsnB2CData[key] = {
                    hsn_sc: row.hsn,
                    desc: '',
                    uqc: row.uqc,
                    qty: 0,
                    rt: rt,
                    txval: 0,
                    iamt: 0,
                    samt: 0,
                    camt: 0,
                    csamt: 0
                }
            }
            hsnB2CData[key].qty += parseFloat(row.quantity) || 0
            hsnB2CData[key].txval += Math.round(row.taxableValue * 100) / 100
            hsnB2CData[key].iamt += Math.round(row.igst * 100) / 100
            hsnB2CData[key].samt += Math.round(row.sgst * 100) / 100
            hsnB2CData[key].camt += Math.round(row.cgst * 100) / 100
            hsnB2CData[key].csamt += Math.round(row.cess * 100) / 100
        })

        const hsnB2BArray = Object.values(hsnB2BData).map((hsn, index) => ({
            num: index + 1,
            hsn_sc: hsn.hsn_sc,
            desc: hsn.desc,
            uqc: hsn.uqc,
            qty: hsn.qty,
            rt: hsn.rt,
            txval: Math.round(hsn.txval * 100) / 100,
            iamt: Math.round(hsn.iamt * 100) / 100,
            samt: Math.round(hsn.samt * 100) / 100,
            camt: Math.round(hsn.camt * 100) / 100,
            csamt: Math.round(hsn.csamt * 100) / 100
        }))

        const hsnB2CArray = Object.values(hsnB2CData).map((hsn, index) => ({
            num: index + 1,
            hsn_sc: hsn.hsn_sc,
            desc: hsn.desc,
            uqc: hsn.uqc,
            qty: hsn.qty,
            rt: hsn.rt,
            txval: Math.round(hsn.txval * 100) / 100,
            iamt: Math.round(hsn.iamt * 100) / 100,
            samt: Math.round(hsn.samt * 100) / 100,
            camt: Math.round(hsn.camt * 100) / 100,
            csamt: Math.round(hsn.csamt * 100) / 100
        }))

        if ((hsnB2BArray.length > 0 || hsnB2CArray.length > 0) && selectedSections.hsn) {
            jsonData.hsn = {}
            if (hsnB2BArray.length > 0) {
                jsonData.hsn.hsn_b2b = hsnB2BArray
            }
            if (hsnB2CArray.length > 0) {
                jsonData.hsn.hsn_b2c = hsnB2CArray
            }
        }

        // Process B2B Credit Notes (cdnr)
        if (b2bCreditNotes.length > 0 && selectedSections.cdnr) {
            const groupedB2BCredit = {}
            b2bCreditNotes.forEach(row => {
                const key = `${row.gstinCustomer}_${row.invoiceNo}`
                if (!groupedB2BCredit[key]) {
                    groupedB2BCredit[key] = {
                        ctin: row.gstinCustomer,
                        nt: [
                            {
                                nt_num: row.invoiceNo,
                                nt_dt: formatDateForJson(row.invoiceDate),
                                ntty: "C",
                                pos: getPosEnum(row.placeOfSupply),
                                rchrg: row.reverseCharge || 'N',
                                inv_typ: "R",
                                itms: [],
                                itmsGroupedByRate: {}
                            }
                        ]
                    }
                }

                const ntRef = groupedB2BCredit[key].nt[0]
                const rateKey = String(row.rate)
                if (!ntRef.itmsGroupedByRate[rateKey]) {
                    let rt = parseFloat(row.rate) || 0
                    const rateStr = rt.toString().replace('.', '')
                    const numCode = parseInt(`${rateStr}01`, 10)
                    ntRef.itmsGroupedByRate[rateKey] = {
                        num: numCode,
                        rt: rt,
                        txval: 0,
                        csamt: 0,
                        iamt: 0,
                        camt: 0,
                        samt: 0
                    }
                }
                const agg = ntRef.itmsGroupedByRate[rateKey]
                agg.txval += Math.round(Math.abs(row.taxableValue) * 100) / 100
                agg.csamt += Math.round(Math.abs(row.cess) * 100) / 100
                agg.iamt += Math.round(Math.abs(row.igst) * 100) / 100
                agg.camt += Math.round(Math.abs(row.cgst) * 100) / 100
                agg.samt += Math.round(Math.abs(row.sgst) * 100) / 100
            })

            jsonData.cdnr = Object.values(groupedB2BCredit).map(group => ({
                ctin: group.ctin,
                nt: group.nt.map(nt => {
                    const items = Object.values(nt.itmsGroupedByRate).map(item => ({
                        num: item.num,
                        itm_det: {
                            txval: Math.round(item.txval * 100) / 100,
                            rt: item.rt,
                            csamt: Math.round(item.csamt * 100) / 100,
                            iamt: Math.round(item.iamt * 100) / 100,
                            camt: Math.round(item.camt * 100) / 100,
                            samt: Math.round(item.samt * 100) / 100
                        }
                    }))

                    const totalVal = Math.round(
                        items.reduce((sum, item) => {
                            const det = item.itm_det
                            return sum + det.txval + det.iamt + det.camt + det.samt + det.csamt
                        }, 0) * 100
                    ) / 100

                    return {
                        nt_num: nt.nt_num,
                        nt_dt: nt.nt_dt,
                        ntty: nt.ntty,
                        val: totalVal,
                        pos: nt.pos,
                        rchrg: nt.rchrg,
                        inv_typ: nt.inv_typ,
                        itms: items
                    }
                })
            }))
        }

        // Process B2CL Credit Notes (cdnur)
        if (b2clCreditNotes.length > 0 && selectedSections.cdnur) {
            const groupedB2CLCredit = {}
            b2clCreditNotes.forEach(row => {
                const key = row.invoiceNo
                if (!groupedB2CLCredit[key]) {
                    groupedB2CLCredit[key] = {
                        nt_num: row.invoiceNo,
                        nt_dt: formatDateForJson(row.invoiceDate),
                        ntty: "C",
                        typ: "B2CL",
                        pos: getPosEnum(row.placeOfSupply),
                        itmsGroupedByRate: {}
                    }
                }

                const ntRef = groupedB2CLCredit[key]
                const rateKey = String(row.rate)
                if (!ntRef.itmsGroupedByRate[rateKey]) {
                    let rt = parseFloat(row.rate) || 0
                    ntRef.itmsGroupedByRate[rateKey] = {
                        num: Object.keys(ntRef.itmsGroupedByRate).length + 1,
                        rt: rt,
                        txval: 0,
                        csamt: 0,
                        iamt: 0,
                        camt: 0,
                        samt: 0
                    }
                }

                const agg = ntRef.itmsGroupedByRate[rateKey]
                agg.txval += Math.round(Math.abs(row.taxableValue) * 100) / 100
                agg.csamt += Math.round(Math.abs(row.cess) * 100) / 100
                agg.iamt += Math.round(Math.abs(row.igst) * 100) / 100
                agg.camt += Math.round(Math.abs(row.cgst) * 100) / 100
                agg.samt += Math.round(Math.abs(row.sgst) * 100) / 100
            })

            jsonData.cdnur = Object.values(groupedB2CLCredit).map(creditNote => {
                const items = Object.values(creditNote.itmsGroupedByRate).map(item => ({
                    num: item.num,
                    itm_det: {
                        txval: Math.round(item.txval * 100) / 100,
                        rt: item.rt,
                        csamt: Math.round(item.csamt * 100) / 100,
                        iamt: Math.round(item.iamt * 100) / 100,
                        camt: Math.round(item.camt * 100) / 100,
                        samt: Math.round(item.samt * 100) / 100
                    }
                }))

                const totalVal = Math.round(
                    items.reduce((sum, item) => {
                        const det = item.itm_det
                        return sum + det.txval + det.iamt + det.camt + det.samt + det.csamt
                    }, 0) * 100
                ) / 100

                return {
                    nt_num: creditNote.nt_num,
                    nt_dt: creditNote.nt_dt,
                    ntty: creditNote.ntty,
                    val: totalVal,
                    typ: creditNote.typ,
                    pos: creditNote.pos,
                    itms: items
                }
            })
        }

        // Process Export Credit Notes (cdnur)
        if (exportCreditNotes.length > 0 && selectedSections.cdnur) {
            const groupedExportCredit = {}
            exportCreditNotes.forEach(row => {
                const key = row.invoiceNo
                if (!groupedExportCredit[key]) {
                    const typ = row.invoiceType === 'Export with payment of tax' ? 'EXPWP' : 'EXPWOP'
                    groupedExportCredit[key] = {
                        nt_num: row.invoiceNo,
                        nt_dt: formatDateForJson(row.invoiceDate),
                        ntty: "C",
                        val: Math.round(Math.abs(row.invoiceValue) * 100) / 100,
                        typ: typ,
                        itms: []
                    }
                }

                const item = {
                    num: groupedExportCredit[key].itms.length + 1,
                    itm_det: {
                        txval: Math.round(Math.abs(row.taxableValue) * 100) / 100,
                        rt: parseFloat(row.rate) || 0,
                        csamt: Math.round(Math.abs(row.cess) * 100) / 100,
                        iamt: row.invoiceType === 'Export Without payment of tax'
                            ? 0
                            : Math.round(Math.abs(row.igst) * 100) / 100
                    }
                }
                groupedExportCredit[key].itms.push(item)
            })

            const exportCreditNotesArray = Object.values(groupedExportCredit).map(creditNote => ({
                nt_num: creditNote.nt_num,
                nt_dt: creditNote.nt_dt,
                ntty: creditNote.ntty,
                val: creditNote.val,
                typ: creditNote.typ,
                itms: creditNote.itms.map(item => ({
                    num: item.num,
                    itm_det: item.itm_det
                }))
            }))

            if (jsonData.cdnur) {
                jsonData.cdnur = [...jsonData.cdnur, ...exportCreditNotesArray]
            } else {
                jsonData.cdnur = exportCreditNotesArray
            }
        }

        // Add document issued data
        if (selectedSections.doc_issue) {
            const regularInvoiceNumbers = regularInvoices.map(row => row.invoiceNo).filter(inv => inv && inv.trim() !== '')
            const uniqueRegularInvoices = [...new Set(regularInvoiceNumbers)]

            const creditNoteNumbers = creditNotes.map(row => row.invoiceNo).filter(inv => inv && inv.trim() !== '')
            const uniqueCreditNotes = [...new Set(creditNoteNumbers)]

            const doc_det = []

            if (uniqueRegularInvoices.length > 0) {
                const groupedInvoices = groupInvoicesByPrefix(uniqueRegularInvoices)
                const docsArray = []
                Object.keys(groupedInvoices).forEach((prefix, prefixIndex) => {
                    const group = groupedInvoices[prefix]
                    const sortedGroup = group.sort((a, b) => {
                        const numA = extractNumericPart(a)
                        const numB = extractNumericPart(b)
                        return numA - numB
                    })

                    docsArray.push({
                        num: prefixIndex + 1,
                        to: sortedGroup[sortedGroup.length - 1],
                        from: sortedGroup[0],
                        totnum: sortedGroup.length,
                        cancel: 0,
                        net_issue: sortedGroup.length
                    })
                })

                doc_det.push({
                    doc_num: 1, // Fixed GST document ID for Regular Invoices
                    doc_typ: "Invoices for outward supply",
                    docs: docsArray
                })
            }

            if (uniqueCreditNotes.length > 0) {
                const groupedCreditNotes = groupInvoicesByPrefix(uniqueCreditNotes)
                const docsArrayCN = []
                Object.keys(groupedCreditNotes).forEach((prefix, prefixIndex) => {
                    const group = groupedCreditNotes[prefix]
                    const sortedGroup = group.sort((a, b) => {
                        const numA = extractNumericPart(a)
                        const numB = extractNumericPart(b)
                        return numA - numB
                    })

                    docsArrayCN.push({
                        num: prefixIndex + 1,
                        to: sortedGroup[sortedGroup.length - 1],
                        from: sortedGroup[0],
                        totnum: sortedGroup.length,
                        cancel: 0,
                        net_issue: sortedGroup.length
                    })
                })

                doc_det.push({
                    doc_num: 5, // Fixed GST document ID for Credit Note
                    doc_typ: "Credit Note",
                    docs: docsArrayCN
                })
            }

            if (doc_det.length > 0) {
                jsonData.doc_issue = {
                    doc_det: doc_det
                }
            }
        }

        // Add export section
        const exportTransactions = gstinData.filter(row => isExportInvoiceType(row.invoiceType))
        if (exportTransactions.length > 0 && selectedSections.exp) {
            const exportData = {}
            exportTransactions.forEach(row => {
                const expType = getInvoiceTypeCode(row.invoiceType)
                if (!exportData[expType]) exportData[expType] = {}

                const invKey = row.invoiceNo
                if (!exportData[expType][invKey]) {
                    exportData[expType][invKey] = {
                        inum: row.invoiceNo,
                        idt: formatDateForJson(row.invoiceDate),
                        val: 0,
                        itmsGroupedByRate: {}
                    }
                }

                // Add to total invoice value
                exportData[expType][invKey].val = Math.round((exportData[expType][invKey].val + Math.abs(parseFloat(row.invoiceValue) || 0)) * 100) / 100

                const invRef = exportData[expType][invKey]
                const rateKey = String(row.rate)
                if (!invRef.itmsGroupedByRate[rateKey]) {
                    invRef.itmsGroupedByRate[rateKey] = {
                        rt: parseFloat(row.rate) || 0,
                        txval: 0,
                        iamt: 0,
                        csamt: 0
                    }
                }
                const agg = invRef.itmsGroupedByRate[rateKey]
                agg.txval += Math.round(row.taxableValue * 100) / 100
                const invTypeCode = getInvoiceTypeCode(row.invoiceType)
                agg.iamt += invTypeCode === 'WOPAY' ? 0 : Math.round(row.igst * 100) / 100
                agg.csamt += Math.round(row.cess * 100) / 100
            })

            jsonData.exp = Object.entries(exportData).map(([exp_typ, invMap]) => ({
                exp_typ,
                inv: Object.values(invMap).map(inv => ({
                    inum: inv.inum,
                    idt: inv.idt,
                    val: inv.val,
                    itms: Object.values(inv.itmsGroupedByRate).map((g) => ({
                        txval: Math.round(g.txval * 100) / 100,
                        rt: g.rt,
                        iamt: Math.round(g.iamt * 100) / 100,
                        csamt: Math.round(g.csamt * 100) / 100
                    }))
                }))
            }))
        }

        // Add SEZ with payment
        const sezWithPaymentTransactions = gstinData.filter(row =>
            getInvoiceTypeCode(row.invoiceType) === 'SEWP'
        )
        if (sezWithPaymentTransactions.length > 0 && selectedSections.sezwp) {
            jsonData.sezwp = sezWithPaymentTransactions.map(row => ({
                inum: row.invoiceNo,
                idt: formatDateForJson(row.invoiceDate),
                val: Math.round(row.invoiceValue * 100) / 100,
                itms: [{
                    txval: Math.round(row.taxableValue * 100) / 100,
                    rt: parseFloat(row.rate) || 0,
                    iamt: Math.round(row.igst * 100) / 100,
                    csamt: Math.round(row.cess * 100) / 100
                }]
            }))
        }

        // Add SEZ without payment
        const sezWithoutPaymentTransactions = gstinData.filter(row =>
            getInvoiceTypeCode(row.invoiceType) === 'SEWOP'
        )
        if (sezWithoutPaymentTransactions.length > 0 && selectedSections.sezwop) {
            jsonData.sezwop = sezWithoutPaymentTransactions.map(row => ({
                inum: row.invoiceNo,
                idt: formatDateForJson(row.invoiceDate),
                val: Math.round(row.invoiceValue * 100) / 100,
                itms: [{
                    txval: Math.round(row.taxableValue * 100) / 100,
                    rt: parseFloat(row.rate) || 0,
                    iamt: 0,
                    csamt: Math.round(row.cess * 100) / 100
                }]
            }))
        }

        // Add nil section
        const taxExemptTransactions = gstinData.filter(row =>
            (isTaxExemptInvoiceType(row.invoiceType) || parseFloat(row.rate) === 0) && !isExportInvoiceType(row.invoiceType)
        )

        if (taxExemptTransactions.length > 0 && selectedSections.nil) {
            const nilData = {
                INTRB2B: { expt_amt: 0, nil_amt: 0, ngsup_amt: 0 },
                INTRAB2B: { expt_amt: 0, nil_amt: 0, ngsup_amt: 0 },
                INTRB2C: { expt_amt: 0, nil_amt: 0, ngsup_amt: 0 },
                INTRAB2C: { expt_amt: 0, nil_amt: 0, ngsup_amt: 0 }
            }

            taxExemptTransactions.forEach(row => {
                const isB2B = row.gstinCustomer && row.gstinCustomer.trim() !== ''
                const isInterState = isInterStateTransaction(row.gstinMaster, row.placeOfSupply)
                const invTypeCode = getInvoiceTypeCode(row.invoiceType)
                const isExport = invTypeCode === 'WOPAY' || invTypeCode === 'WPAY'
                const isExempt = row.invoiceType === 'Exempt' || invTypeCode === 'EXP'
                const isNonGST = row.invoiceType === 'Non-GST Supply' || invTypeCode === 'NGSUP'
                const isSEZWOP = invTypeCode === 'SEWOP'

                let supplyType = ''
                if (isB2B && isInterState) supplyType = 'INTRB2B'
                else if (isB2B && !isInterState) supplyType = 'INTRAB2B'
                else if (!isB2B && isInterState) supplyType = 'INTRB2C'
                else if (!isB2B && !isInterState) supplyType = 'INTRAB2C'

                if (supplyType) {
                    if (isExport || isSEZWOP) {
                        nilData[supplyType].expt_amt += (parseFloat(row.taxableValue) || 0)
                    } else if (isExempt) {
                        nilData[supplyType].expt_amt += (parseFloat(row.taxableValue) || 0)
                    } else if (isNonGST) {
                        nilData[supplyType].ngsup_amt += (parseFloat(row.taxableValue) || 0)
                    } else {
                        // Fallback for nil rated (rate=0)
                        nilData[supplyType].nil_amt += (parseFloat(row.taxableValue) || 0)
                    }
                }
            })

            jsonData.nil = {
                inv: Object.entries(nilData)
                    .filter(([_, data]) => data.expt_amt !== 0 || data.nil_amt !== 0 || data.ngsup_amt !== 0)
                    .map(([sply_ty, data]) => ({
                        sply_ty,
                        expt_amt: Math.round(data.expt_amt * 100) / 100,
                        nil_amt: Math.round(data.nil_amt * 100) / 100,
                        ngsup_amt: Math.round(data.ngsup_amt * 100) / 100
                    }))
            }
        }

        return jsonData
    } catch (error) {
        console.error('Error generating unified JSON:', error)
        return null
    }
}
